import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Calendar, Check, ArrowRight, Clock } from "lucide-react";

export default function CoachDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/coach/me/summary").then((r) => setSummary(r.data)),
      api.get("/coach/my-batches").then((r) => setBatches(r.data)),
    ])
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="coach-dashboard">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Coach Command Centre</div>
      <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">
        Hi <span className="brand-blue">{user?.name?.split(" ")[0] || "Coach"}</span>.
      </h1>
      <p className="text-white/60 mt-3 text-sm">Pick a batch to mark attendance or view parent contacts.</p>

      {/* KPIs */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat icon={Calendar} label="Active batches" value={summary?.batch_count ?? "—"} testid="stat-batches" />
        <Stat icon={Users} label="Active wards" value={summary?.active_wards ?? "—"} testid="stat-wards" tone="orange" />
        <Stat icon={Check} label="Marked today" value={summary?.marked_today ?? "—"} testid="stat-marked" tone="lime" />
      </div>

      {/* Batch cards */}
      <div className="mt-12">
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4">My Batches</h2>

        {loading && <div className="text-white/40 py-6 font-mono uppercase text-xs">Loading…</div>}

        {!loading && batches.length === 0 && (
          <div className="border border-white/12 bg-[#131A2B] p-12 text-center" data-testid="coach-empty">
            <Users size={32} className="mx-auto mb-3 opacity-40" />
            <div className="text-white/60 mb-1">No batches assigned yet.</div>
            <div className="text-xs font-mono text-white/40">Ask the academy admin to assign you a batch from the Admin ERP.</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {batches.map((b) => (
            <Link
              key={b.id}
              to={`/coach/batches/${b.id}`}
              data-testid={`coach-batch-${b.id}`}
              className="border border-white/12 bg-[#131A2B] p-6 hover:border-brand-blue hover-lift group block"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{b.sport} · {b.age_group}</div>
                  <div className="font-display text-2xl uppercase font-bold mt-1 truncate">{b.name}</div>
                  <div className="text-xs text-white/50 mt-2 flex items-center gap-2">
                    <Clock size={12} /> {b.time_start} – {b.time_end} · {(b.days || []).join(", ")}
                  </div>
                </div>
                <ArrowRight size={20} className="text-white/40 group-hover:brand-blue group-hover:translate-x-1 transition" />
              </div>
              <div className="mt-5 flex items-center justify-between text-xs font-mono uppercase tracking-widest">
                <div><span className="brand-blue text-lg font-display font-black">{b.enrolled_count ?? 0}</span> <span className="text-white/40">wards</span></div>
                <div className="text-white/40">Capacity: {b.capacity}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, tone }) {
  const cls = tone === "orange" ? "brand-orange" : tone === "lime" ? "text-emerald-400" : "brand-blue";
  return (
    <div data-testid={testid} className="border border-white/12 bg-[#131A2B] p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon size={16} className={cls} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</span>
      </div>
      <div className={`font-display text-3xl font-black ${cls}`}>{value}</div>
    </div>
  );
}
